/**
 * maskRestrictedEvents — pure helper that wraps an event so PII / detail
 * fields are obscured when the user lacks `calendarManageAnothers` and the
 * event (or its underlying resource) is marked restricted.
 *
 * Why a separate util: this exact masking happens in three places (chip
 * label, "show all" modal, and any future tooltip). Pulling the rule into a
 * pure function keeps the React layer dumb and lets us test it without
 * mounting any components.
 *
 * Consumers pass the perms object returned by `useCalendarPermissions()`
 * (which already exposes `shouldMaskRestricted` + `canManageAnothers`).
 *
 * @module maskRestrictedEvents
 */

const MASKED_TITLE = 'Restricted';
const MASKED_PILOT = '—';

/**
 * Returns a shallow copy of `event` with PII/details replaced when masking
 * applies. The original event is not mutated.
 *
 * @param {Object} event
 * @param {{shouldMaskRestricted: (event: Object) => boolean}} perms
 * @returns {Object} Either the original event reference (no masking) or a
 *                   shallow copy with `restricted` fields blanked.
 */
export function maskRestrictedEvent(event, perms) {
	if (!event) return event;
	if (!perms || typeof perms.shouldMaskRestricted !== 'function') return event;
	if (!perms.shouldMaskRestricted(event)) return event;

	return {
		...event,
		title: MASKED_TITLE,
		description: '',
		pilotName: MASKED_PILOT,
		pilotSurname: '',
		instructorName: MASKED_PILOT,
		instructorSurname: '',
		comments: '',
		notes: '',
		// The aircraft identifiers a chip/row/bar prints on its face. The backend
		// does NOT mask (feed carries only a `restricted` boolean), so clearing
		// these here is the single guard against a masked event still leaking its
		// tail number or resource name through a `reg || resourceName` fallback on
		// any surface — present or future — that consumes this util.
		registrationNumber: '',
		regNumb: '',
		resourceName: '',
		_masked: true
	};
}

/**
 * Convenience: bulk-mask an array of events.
 *
 * @param {Array<Object>} events
 * @param {{shouldMaskRestricted: (event: Object) => boolean}} perms
 * @returns {Array<Object>}
 */
export function maskRestrictedEvents(events, perms) {
	if (!Array.isArray(events)) return [];
	return events.map(ev => maskRestrictedEvent(ev, perms));
}

/**
 * Is this event restricted FOR THE CURRENT VIEWER — i.e. must its detail modal
 * stay shut and no `getEvent` fetch fire (P2)?
 *
 * Accepts either a raw feed event or one already run through
 * `maskRestrictedEvent`: an already-masked object carries `_masked`, a raw one
 * is judged by `perms.shouldMaskRestricted`. Fail-closed by construction —
 * `shouldMaskRestricted` returns true for a restricted event whenever the viewer
 * lacks `canManageAnothers`, which is the default while perms are still loading,
 * so a restricted event is gated from the very first render.
 *
 * @param {?Object} event
 * @param {?{shouldMaskRestricted?: (event: Object) => boolean}} perms
 * @returns {boolean}
 */
export function isRestrictedForViewer(event, perms) {
	if (!event) return false;
	if (event._masked === true) return true;
	if (perms && typeof perms.shouldMaskRestricted === 'function') {
		return perms.shouldMaskRestricted(event) === true;
	}
	return false;
}

export const MASKED_VALUES = {
	title: MASKED_TITLE,
	pilot: MASKED_PILOT
};
